import { useEffect, useMemo } from 'react'

import { AppProps } from 'next/app'
import { useRouter } from 'next/router'

import { ApolloProvider } from '@apollo/client'

import { CookieBanner } from '@ui/components/consent'
import { Seo } from '@ui/components/seo'
import { ErrorBoundary } from '@ui/lib/airbrake/client'
import { honorSignal } from '@ui/lib/consent'
import { canonicalMarketingUrl } from '@ui/lib/urls'

import { MeasurementTags } from '@website/components/MeasurementTags'
import { EventTrackingProvider } from '@website/context/EventTrackingContext'
import { initializeApollo } from '@website/lib/apolloClient'
import { paytoneOne, sourceSans, sourceSerif } from '@website/styles/fonts'
import type { NextPageWithLayout } from '@website/types'
import { getClientId, getSessionId } from '@website/utils/session'
import { captureUtmFromUrl } from '@website/utils/utm'

// Load Shared Tailwind CSS
import '@ui/styles/tailwind.css'

const DEFAULT_TITLE = 'ATM.com - Earn, Budget, Save & Access Cash'
const DEFAULT_DESCRIPTION =
  'Earn extra cash, budget smarter, and access instant cash advances with ATM.com.'

function Website({
  Component,
  pageProps,
}: AppProps & { Component: NextPageWithLayout }) {
  const apolloClient = useMemo(() => initializeApollo(), [])
  const getLayout = Component.getLayout ?? ((page) => page)
  const { asPath } = useRouter()
  // Query/hash stripped; a page's own <Seo> overrides by key.
  const canonical = canonicalMarketingUrl(asPath.split(/[?#]/)[0])

  // First-party boot: our ids and attribution, and the signal's consent record.
  useEffect(() => {
    getClientId()
    getSessionId()
    captureUtmFromUrl()
    honorSignal()
  }, [])

  return (
    <ErrorBoundary>
      {/* Fonts on <html>, not a wrapper, so portaled dialogs inherit them. */}
      <style jsx global>{`
        html {
          --font-primary: ${sourceSans.style.fontFamily};
          --font-serif: ${sourceSerif.style.fontFamily};
          --font-display: ${paytoneOne.style.fontFamily};
          font-family: var(--font-primary);
        }
      `}</style>
      <Seo
        title={DEFAULT_TITLE}
        description={DEFAULT_DESCRIPTION}
        canonical={canonical}
        image="/img/og/og-atm-dark-logo.png"
        imageWidth={1920}
        imageHeight={938}
      />
      <ApolloProvider client={apolloClient}>
        <EventTrackingProvider>
          {getLayout(<Component {...pageProps} />)}
        </EventTrackingProvider>
      </ApolloProvider>
      <MeasurementTags />
      <CookieBanner />
    </ErrorBoundary>
  )
}

export default Website
